const SSO_ATTEMPT_KEY = 'sso_attempt_id';
const SSO_EMAIL_KEY = 'sso_attempt_email';
const TELEMETRY_URL = process.env.VUE_APP_API_URL + '/auth/sso/telemetry';
const CONFIRM_URL = process.env.VUE_APP_API_URL + '/auth/sso/confirm';

export function getSsoAttemptId() {
    try {
        return sessionStorage.getItem(SSO_ATTEMPT_KEY);
    } catch (e) {
        return null;
    }
}

export function setSsoAttemptId(id) {
    if (!id) return;
    try {
        sessionStorage.setItem(SSO_ATTEMPT_KEY, id);
    } catch (e) {
        // ignore storage errors
    }
}

export function getSsoEmail() {
    try {
        return sessionStorage.getItem(SSO_EMAIL_KEY);
    } catch (e) {
        return null;
    }
}

export function setSsoEmail(email) {
    if (!email) return;
    try {
        sessionStorage.setItem(SSO_EMAIL_KEY, email);
    } catch (e) {
        // ignore
    }
}

export function clearSsoAttemptId() {
    try {
        sessionStorage.removeItem(SSO_ATTEMPT_KEY);
        sessionStorage.removeItem(SSO_EMAIL_KEY);
    } catch (e) {
        // ignore
    }
}

/**
 * Clear leftover SSO / auth browser state before a new Microsoft redirect.
 * Stale localStorage or a previous attempt can conflict with a fresh Entra bounce.
 */
export function prepareFreshSsoLogin(email = null) {
    clearSsoAttemptId();
    try {
        localStorage.removeItem('ssoToken');
        localStorage.removeItem('ssoUser');
    } catch (e) {
        // ignore
    }
    if (email) {
        setSsoEmail(email);
    }
}

/** Known AMS auth / SSO keys persisted in the browser for this origin. */
const AMS_APP_STORAGE_KEYS = [
    'token',
    'loggedUser',
    'isGodUser',
    'permissionList',
    'productList',
    'selectedProduct',
    'ttl',
    'timer',
    'companyLogo',
    'activeCompany',
    'ssoToken',
    'ssoUser',
    SSO_ATTEMPT_KEY,
    SSO_EMAIL_KEY,
];

/**
 * Clear AMS app storage for this origin only.
 * Cannot clear Microsoft Entra cookies or the whole browser cache (browser security).
 *
 * IMPORTANT: Call AFTER resetting Pinia/useLocalStorage in-memory state.
 * If you only removeItem while Pinia still holds old values, VueUse writes them back.
 */
export function clearAmsAppBrowserState({ wipeAllOriginStorage = true } = {}) {
    try {
        if (wipeAllOriginStorage) {
            // AMS SPA owns this origin — full wipe so DevTools Application shows empty.
            localStorage.clear();
            sessionStorage.clear();
        } else {
            AMS_APP_STORAGE_KEYS.forEach((key) => {
                localStorage.removeItem(key);
                sessionStorage.removeItem(key);
            });
        }
    } catch (e) {
        // ignore
    }

    // Drop Cache API entries for this origin if present (PWA / fetched assets).
    try {
        if (typeof caches !== 'undefined' && caches.keys) {
            caches.keys().then((names) => {
                names.forEach((name) => caches.delete(name));
            }).catch(() => {});
        }
    } catch (e) {
        // ignore
    }
}

/**
 * Reset in-memory auth/SSO stores, then wipe origin localStorage/sessionStorage.
 * Order matters — stores first, storage second.
 */
export function purgeAmsClientState(authStore, ssoStore = null, { keepEmail = false } = {}) {
    const preservedEmail = keepEmail && ssoStore?.email ? ssoStore.email : null;
    try {
        authStore?.clearLocalSession?.();
    } catch (e) {
        // ignore
    }
    try {
        if (ssoStore) {
            ssoStore.token = null;
            ssoStore.user = {};
            ssoStore.loader = false;
            ssoStore.showLoginFields = false;
            // Do not wipe email mid-login — /auth/available needs it.
            if (!keepEmail) {
                ssoStore.email = null;
            }
        }
    } catch (e) {
        // ignore
    }
    clearAmsAppBrowserState({ wipeAllOriginStorage: true });
    if (preservedEmail && ssoStore) {
        ssoStore.email = preservedEmail;
        setSsoEmail(preservedEmail);
    }
}

/**
 * Fire-and-forget frontend SSO telemetry with full NWFC triage context.
 * context: {
 *   email, failure_reason, detail, http_status, backend_status, backend_message,
 *   user_id, company_id, product_count, has_code, has_state, error_name, href
 * }
 */
export async function sendSsoTelemetry(stage, context = {}) {
    const ssoAttemptId = getSsoAttemptId();
    const email = context.email || getSsoEmail() || null;

    // Still report failures even if attempt id was lost — backend can attach/create
    if (!ssoAttemptId && !email) return;

    const payload = {
        sso_attempt_id: ssoAttemptId || undefined,
        email,
        stage,
        detail: context.detail || context.failure_reason || null,
        failure_reason: context.failure_reason || null,
        http_status: context.http_status ?? null,
        backend_status: context.backend_status ?? null,
        backend_message: context.backend_message ?? null,
        user_id: context.user_id ?? null,
        company_id: context.company_id ?? null,
        product_count: context.product_count ?? null,
        has_code: context.has_code ?? null,
        has_state: context.has_state ?? null,
        has_token: context.has_token ?? null,
        success: context.success ?? null,
        error_name: context.error_name ?? null,
        href: typeof window !== 'undefined' ? window.location.href : null,
        platform: 'ams',
    };

    try {
        const response = await fetch(TELEMETRY_URL, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Accept': 'application/json',
            },
            body: JSON.stringify(payload),
        });
        const result = await response.json().catch(() => ({}));
        if (result?.sso_attempt_id) {
            setSsoAttemptId(result.sso_attempt_id);
        }
    } catch (e) {
        // intentionally ignore — detection must not break login UX
    }
}

/**
 * Confirm frontend session after successful SSO setup. Fire-and-forget.
 */
export async function confirmSsoSession(token) {
    const ssoAttemptId = getSsoAttemptId();
    if (!ssoAttemptId) return;

    try {
        const headers = {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
        };
        if (token) {
            headers.Authorization = `Bearer ${token}`;
        }
        await fetch(CONFIRM_URL, {
            method: 'POST',
            headers,
            body: JSON.stringify({
                sso_attempt_id: ssoAttemptId,
                email: getSsoEmail() || undefined,
            }),
        });
        clearSsoAttemptId();
    } catch (e) {
        // intentionally ignore
    }
}
